// src/app/services/transaction.service.ts
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface Transaction {
  transactionId: string;
  userId: string;
  transactionType: 'CREDIT' | 'DEBIT';
  amount: number;
  sourceType: 'INVOICE' | 'ORDER' | 'SYSTEM' | 'ADMIN';
  sourceId?: string;
  description: string;
  createdAt: string;
  businessHub?: {
    id: number;
    name: string;
    avatar?: string;
  };
  invoice?: {
    invoiceId: string;
    companyName?: string;
    totalAmount: number;
    invoiceDate?: string;
    downloadUrl?: string;
    taxId: string;
  };
  metadata?: any;
}

export interface TransactionStats {
  monthlyEarned: number;
  monthlySpent: number;
  totalBalance: number;
  totalEarned: number;
  totalSpent: number;
  topSources: {
    category: string;
    businessHubId: number;
    amount: number;
    percentage: number;
    transactionCount: number;
    color: string;
    icon: string;
  }[];
}

export interface TransactionListResponse {
  transactions: Transaction[];
  total: number;
  limit: number;
  offset: number;
}

export interface FormattedTransaction {
  id: string;
  type: 'verdient' | 'ausgegeben';
  amount: number;
  description: string;
  businessName?: string;
  businessAvatar?: string;
  businessHubId?: number;
  category: string;
  date: Date;
  icon: string;
  color: string;
  invoice?: {
    invoiceId: string;
    companyName?: string;
    totalAmount: number;
    invoiceDate?: string;
    downloadUrl?: string;
    taxId: string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class TransactionService {
  private apiUrl = `${environment.apiUrl}`;

  constructor(private http: HttpClient) { }

  /**
   * Get user transactions
   */
  getTransactions(
    limit: number = 20,
    offset: number = 0
  ): Observable<TransactionListResponse> {
    const url = `${this.apiUrl}/experience/smartcoins?limit=${limit}&offset=${offset}`;
    return this.http.get<TransactionListResponse>(url);
  }

  /**
   * Get user balance
   */
  async getBalance(): Promise<{ balance: number; availableBalance: number; pendingBalance: number; userId: string }> {
    const response = await firstValueFrom(
      this.http.get<{ balance: number; availableBalance: number; pendingBalance: number; userId: string }>(`${this.apiUrl}/experience/smartcoins/balance`)
    );
    return response;
  }

  /**
   * Get transaction statistics
   */
  getStats(): Observable<TransactionStats> {
    return this.http.get<TransactionStats>(`${this.apiUrl}/experience/smartcoins/stats`);
  }

  /**
   * Format transaction for display
   */
  formatTransaction(transaction: Transaction): FormattedTransaction {
    const isEarned = transaction.transactionType === 'CREDIT';
    
    // Build description based on source type
    let description = '';
    let businessName = '';
    
    if (transaction.businessHub) {
      businessName = transaction.businessHub.name;
      
      if (transaction.sourceType === 'ORDER') {
        description = isEarned 
          ? `Cashback von ${businessName}`
          : `Bezahlung bei ${businessName}`;
      } else if (transaction.sourceType === 'INVOICE') {
        description = `Rechnung von ${businessName}`;
      } else {
        description = isEarned 
          ? `Smart Coins verdient bei ${businessName}`
          : `Smart Coins ausgegeben bei ${businessName}`;
      }
    } else {
      // Fallback if no business hub
      if (transaction.sourceType === 'ORDER') {
        description = isEarned ? 'Cashback verdient' : 'Bezahlung';
      } else if (transaction.sourceType === 'INVOICE') {
        description = 'Rechnung gescannt';
      } else if (transaction.sourceType === 'SYSTEM') {
        description = 'System-Transaktion';
      } else {
        description = isEarned ? 'Smart Coins verdient' : 'Smart Coins ausgegeben';
      }
    }
    
    return {
      id: transaction.transactionId,
      type: isEarned ? 'verdient' : 'ausgegeben',
      amount: transaction.amount,
      description: description,
      businessName: transaction.businessHub?.name,
      businessAvatar: transaction.businessHub?.avatar,
      businessHubId: transaction.businessHub?.id,
      category: this.getCategoryFromSourceType(transaction.sourceType),
      date: new Date(transaction.createdAt),
      icon: this.getIconForSourceType(transaction.sourceType),
      color: isEarned ? 'text-green-600' : 'text-red-600',
      invoice: transaction.invoice
    };
  }

  private getCategoryFromSourceType(sourceType: string): string {
    switch (sourceType) {
      case 'ORDER': return 'order';
      case 'INVOICE': return 'invoice';
      case 'SYSTEM': return 'system';
      case 'ADMIN': return 'admin';
      default: return 'other';
    }
  }

  private getIconForSourceType(sourceType: string): string {
    switch (sourceType) {
      case 'ORDER': return 'pi-shopping-cart';
      case 'INVOICE': return 'pi-file';
      case 'SYSTEM': return 'pi-cog';
      case 'ADMIN': return 'pi-star';
      default: return 'pi-circle-fill';
    }
  }
}